import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import { forwardRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import MediaManualAccount from '~/assets/media-manual-account.png';
import Icon from '~/components/data-display/Icon';
import { BaseSelectProps } from '~/components/inputs/Select/Select';
import SimpleSelect, { SimpleSelectOption } from '~/components/inputs/Select/SimpleSelect';
import { useHasHideBalances } from '~/hooks/useFeatureFlag';
import PadlockClosedFill from '~/icons/icon-padlock-closed-fill.svg?react';
import { useAllAccountsQuery } from '~/services/account';
import { Account, AccountStatus, AccountWithConsent } from '~/types/account';
import { isDefined } from '~/types/typeGuards';
import { formatAccountDisplayName, resolveAccountIdentifier, shortensAccountIdentifier } from '~/utils/accounts';
import { ensureDefinedItems } from '~/utils/array';
import { hasPaymentsFlag } from '~/utils/institutions';
import { formatMoney, newMoney } from '~/utils/money';
import { sortObjectsAlphabetically } from '~/utils/sort';
import styles from './BankAccountSelect.module.scss';

export interface BankAccountSelectProps extends BaseSelectProps {
  emptyOption?:
    | string
    | {
        label: string;
        media?: SimpleSelectOption['media'];
        icon?: SimpleSelectOption['Icon'];
      };
  hideNoAccount?: boolean;
  onChange: (value: string | null, account: Account | null) => void;
  value: string | null;
  filterAccount?: <T extends Account>(account: T) => boolean;
  /**
   * Flag to disable selection of accounts with no payments functionality
   */
  disableAccountsWithNoPaymentsFlag?: boolean;
  /**
   * Optional predicate to disable specific accounts with a custom message.
   * Return { disabled: true, message } to disable the account, or false to skip.
   */
  disableAccountPredicate?: (account: AccountWithConsent) => { disabled: boolean; message?: string } | false;
}

interface InstitutionLogoProps {
  icon?: string | null;
  name?: string;
}

const InstitutionLogo = ({ icon, name }: InstitutionLogoProps) => {
  return (
    <img
      src={icon || MediaManualAccount}
      alt={`${name || ''} logo`}
      style={{
        borderRadius: icon ? 0 : '50%',
      }}
    />
  );
};

const LockedInstitutionLogo = () => {
  return (
    <div className={styles.lockedInstitutionLogoContainer}>
      <Icon className={styles.lockedInstitutionLogoContainerIcon} component={PadlockClosedFill} size="sm" />
    </div>
  );
};

function accountToOption(
  account: AccountWithConsent,
  t: ReturnType<typeof useTranslation<'inputs', 'account_filter'>>['t'],
  hasHideBalances: boolean,
  disableAccountsWithNoPaymentsFlag?: boolean,
  disableAccountPredicate?: BankAccountSelectProps['disableAccountPredicate'],
): SimpleSelectOption {
  const name = account.nickname ?? account.consent.institution?.name;
  const identifier = resolveAccountIdentifier(account);
  const shortIdentifier = shortensAccountIdentifier(identifier);

  const option = {
    description:
      account.availableBalance && !hasHideBalances
        ? t('description_label', {
            balanceEur: isDefined(account.availableBalanceEur)
              ? formatMoney(newMoney(account.availableBalanceEur ?? account.availableBalance.amount, 'EUR'))
              : '--',
            balance: formatMoney(account.availableBalance),
            context: account.availableBalance.currency === 'EUR' ? 'EUR' : undefined,
          })
        : '',
    label: name || shortIdentifier ? ensureDefinedItems([name, shortIdentifier]).join(' • ') : '',
    media: <InstitutionLogo icon={account.consent.institution?.iconUrl} name={name} />,
    selectedLabel: formatAccountDisplayName(account),
    value: account.id,
  } as SimpleSelectOption;

  if (account.status === AccountStatus.CONFLICTING) {
    option.isDisabled = true;
    option.description = t('account_conflict');
  }

  if (
    disableAccountsWithNoPaymentsFlag &&
    isDefined(account.consent.institution) &&
    !hasPaymentsFlag(account.consent.institution)
  ) {
    option.isDisabled = true;
    option.media = <LockedInstitutionLogo />;
    option.description = t('payment_account_disabled');
  }

  if (disableAccountPredicate) {
    const result = disableAccountPredicate(account);
    if (result && result.disabled) {
      option.isDisabled = true;
      option.media = <LockedInstitutionLogo />;
      if (result.message) {
        option.description = result.message;
      }
    }
  }

  return option;
}

const BankAccountSelect = forwardRef<HTMLDivElement, BankAccountSelectProps>(
  (
    {
      emptyOption,
      filterAccount = () => true,
      hideNoAccount,
      disableAccountsWithNoPaymentsFlag = false,
      disableAccountPredicate,
      value,
      onChange,
      ...props
    },
    ref,
  ) => {
    const { data: accounts } = useAllAccountsQuery();
    const { t } = useTranslation('inputs', { keyPrefix: 'account_filter' });
    const hasHideBalances = useHasHideBalances();
    useEffect(() => {
      if (!value || value === 'no-account' || !accounts) {
        return;
      }

      const accountExists = accounts.filter(filterAccount).some((account) => account.id === value);

      if (!accountExists) {
        onChange(null, null);
      }
    }, [value, onChange, accounts, filterAccount]);

    const options = useMemo(() => {
      const noAccountOption: SimpleSelectOption = {
        value: 'no-account',
        label: t('none'),
      };

      if (isDefined(emptyOption) && isString(emptyOption)) {
        noAccountOption.label = emptyOption;
      } else {
        noAccountOption.label = emptyOption?.label ?? noAccountOption.label;
        noAccountOption.Icon = emptyOption?.icon;
        noAccountOption.media = emptyOption?.media;
      }

      // If undefined and not an array, return empty array
      if (accounts === undefined || !isObject(accounts)) {
        return [...(hideNoAccount ? [] : [noAccountOption])];
      }

      const accountOptions: SimpleSelectOption[] = accounts
        .filter(filterAccount)
        .map((account) =>
          accountToOption(account, t, hasHideBalances, disableAccountsWithNoPaymentsFlag, disableAccountPredicate),
        );

      return [
        ...(hideNoAccount ? [] : [noAccountOption]),
        ...accountOptions
          .sort(sortObjectsAlphabetically((item) => item.label))
          .sort((a, b) => (a.isDisabled === b.isDisabled ? 0 : a.isDisabled ? 1 : -1)),
      ];
    }, [
      accounts,
      emptyOption,
      filterAccount,
      hasHideBalances,
      hideNoAccount,
      t,
      disableAccountsWithNoPaymentsFlag,
      disableAccountPredicate,
    ]);

    return (
      <SimpleSelect
        {...props}
        value={value}
        onChange={(e) => onChange(e, accounts?.find((x) => x.id === e) ?? null)}
        emptyValue="no-account"
        options={options}
        ref={ref}
      />
    );
  },
);

export default BankAccountSelect;
